// src/components/Gamification/AchievementToast.jsx
import React, { useEffect, useState } from 'react';

/**
 * Achievement toast – iššoka kai atrakinama nauja badge.
 * Props:
 *   achievements – [{ id, name, icon, xp_reward, description }]
 *   onDismiss    – callback kai uždariama
 */
export default function AchievementToast({ achievements = [], onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (achievements.length > 0) {
      setVisible(true);
      setCurrent(0);
      setClosing(false);
    }
  }, [achievements]);

  useEffect(() => {
    if (!visible) return;

    const t = setTimeout(() => {
      if (current < achievements.length - 1) {
        setCurrent(c => c + 1);
      } else {
        handleClose();
      }
    }, 3500);

    return () => clearTimeout(t);
  }, [visible, current, achievements.length]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, 400);
  };

  if (!visible || achievements.length === 0) return null;

  const a = achievements[current];
  if (!a) return null;

  return (
    <div
      className="fixed top-6 right-6 z-[100] max-w-sm"
      style={{
        animation: closing
          ? 'toastSlideOut 0.4s ease-in forwards'
          : 'toastSlideIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards',
      }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl p-5 flex items-center gap-4"
        style={{ border: '3px solid #FFC800' }}
      >
        {/* Icon */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl flex-shrink-0 animate-achievement-pop"
          style={{ background: 'linear-gradient(135deg, #FFF9E6, #FFF0B3)', border: '2px solid #FFC800' }}
        >
          {a.icon}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black uppercase tracking-wider text-yellow-500 mb-0.5">
            🏅 Pasiekimas atrakinta!
          </p>
          <p className="text-base font-black text-gray-800 leading-tight">{a.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{a.description}</p>
          {a.xp_reward > 0 && (
            <div
              className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-white text-xs font-black"
              style={{ background: 'linear-gradient(90deg, #58CC02, #89E219)' }}
            >
              +{a.xp_reward} XP
            </div>
          )}
        </div>

        {/* Close */}
        <button
          onClick={handleClose}
          className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0 transition"
        >
          ✕
        </button>
      </div>

      {/* Multi-achievement dots */}
      {achievements.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {achievements.map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full transition-all"
              style={{ background: i === current ? '#FFC800' : '#E5E5E5' }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
